import { useEffect, useRef, useState } from "react";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 minuti
const WARNING_BEFORE_MS = 60 * 1000;       // avviso 60s prima

// Eventi considerati "attività utente"
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

/**
 * Hook che invoca onIdle dopo IDLE_TIMEOUT_MS di inattività.
 * Mostra un warning negli ultimi WARNING_BEFORE_MS.
 * Torna { warning, remaining, extend } per il countdown UI.
 */
export default function useIdleTimeout({ enabled, onIdle, onWarning }) {
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(IDLE_TIMEOUT_MS);
  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef(0);
  const firedRef = useRef(false);
  const warningRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);

  // keep callback refs fresh without restarting the timer
  useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);
  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
  useEffect(() => { warningRef.current = warning; }, [warning]);

  const extend = () => {
    lastActivityRef.current = Date.now();
    firedRef.current = false;
    setWarning(false);
    warningRef.current = false;
  };

  useEffect(() => {
    if (!enabled) return;
    lastActivityRef.current = Date.now();
    firedRef.current = false;

    const onActivity = () => {
      const now = Date.now();
      // throttle: aggiorna al massimo ogni 2s
      if (now - throttleRef.current < 2000) return;
      throttleRef.current = now;
      // Non resettare il timer se è già in modalità warning (l'utente deve cliccare "Continua")
      if (warningRef.current) return;
      lastActivityRef.current = now;
    };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const tick = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const rem = IDLE_TIMEOUT_MS - elapsed;
      setRemaining(Math.max(0, rem));
      if (rem <= 0) {
        if (!firedRef.current) {
          firedRef.current = true;
          onIdleRef.current?.();
        }
      } else if (rem <= WARNING_BEFORE_MS && !warningRef.current) {
        warningRef.current = true;
        setWarning(true);
        onWarningRef.current?.(Math.round(rem / 1000));
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [enabled]);

  return { warning, remaining, extend };
}
